import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import * as asuntosApi from '@/api/asuntos.api';
import * as frentesApi from '@/api/frentes.api';
import { plata } from '@/lib/athena';

/**
 * EL BUSCADOR — ⌘K / Ctrl+K.
 *
 * Mandato: *"Búsqueda global arriba con atajo ⌘K: busca personas, temas,
 * montos y frases dentro de los mensajes."*
 *
 * ═══ DOS FUENTES, DOS VELOCIDADES ═══
 *
 * Personas, asuntos y frentes se filtran EN EL NAVEGADOR: ya están cargados
 * para las otras pantallas, así que responden en el mismo golpe de tecla.
 *
 * Los mensajes van al SERVIDOR, contra un índice GIN de Postgres. Esos sí
 * necesitan una consulta, y por eso el texto se retrasa 250 ms: sin el
 * retardo, escribir "depósito" manda ocho peticiones.
 *
 * ═══ EL FRAGMENTO LO ARMA POSTGRES ═══
 *
 * `ts_headline` devuelve el trozo del mensaje con lo encontrado entre « », y
 * sabe dónde estaba la palabra aunque el dueño haya escrito el singular y el
 * mensaje tenga el plural. Recortarlo acá habría significado volver a buscar
 * en el navegador lo que la base ya sabía.
 */

interface Resultado {
  id: string;
  tipo: 'asunto' | 'persona' | 'frente' | 'mensaje';
  titulo: string;
  detalle: string;
  ir: string;
}

export function Buscador() {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const campo = useRef<HTMLInputElement | null>(null);
  const navegar = useNavigate();

  // El atajo se registra una vez, en la ventana. Ponerlo en cada pantalla es
  // como se llega a que funcione en cuatro de siete.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAbierto((v) => !v);
      }
      if (e.key === 'Escape') setAbierto(false);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, []);

  useEffect(() => {
    if (abierto) setTimeout(() => campo.current?.focus(), 30);
    else {
      setQ('');
      setCursor(0);
    }
  }, [abierto]);

  const asuntos = useQuery({
    queryKey: ['asuntos', 'buscador'],
    queryFn: () => asuntosApi.getAsuntos({ limite: 300 }),
    enabled: abierto,
  });
  const frentes = useQuery({
    queryKey: ['frentes'],
    queryFn: frentesApi.getFrentes,
    enabled: abierto,
  });

  // El texto se retrasa 250 ms antes de ir al servidor. Los asuntos y las
  // personas ya estan en memoria y responden en el mismo golpe de tecla; los
  // mensajes son una consulta, y dispararla por cada letra manda ocho
  // peticiones para escribir "deposito".
  const [tardio, setTardio] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTardio(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const mensajes = useQuery({
    queryKey: ['buscar-mensajes', tardio],
    queryFn: () => frentesApi.buscarEnMensajes(tardio, 12),
    enabled: abierto && tardio.trim().length >= 2,
  });

  const resultados = useMemo<Resultado[]>(() => {
    const texto = q.trim().toLowerCase();
    if (texto.length < 2) return [];

    const lista = asuntos.data?.asuntos ?? [];
    const salida: Resultado[] = [];

    for (const a of lista) {
      const pajar = [
        a.descripcion,
        a.conQuien,
        a.tema ?? '',
        a.codigo,
        a.montoCentavos ? String(a.montoCentavos / 100) : '',
      ]
        .join(' ')
        .toLowerCase();
      if (!pajar.includes(texto)) continue;
      salida.push({
        id: a.id,
        tipo: 'asunto',
        titulo: a.descripcion,
        detalle: `${a.conQuien}${a.montoCentavos ? ` · ${plata(a.montoCentavos, a.moneda)}` : ''}`,
        ir: `/me-toca`,
      });
    }

    // Personas: una sola vez cada una, aunque tenga cinco asuntos.
    const vistas = new Set<string>();
    for (const a of lista) {
      if (!a.contactId || vistas.has(a.contactId)) continue;
      if (!a.conQuien.toLowerCase().includes(texto)) continue;
      vistas.add(a.contactId);
      salida.push({
        id: a.contactId,
        tipo: 'persona',
        titulo: a.conQuien,
        detalle: 'Ver su ficha',
        ir: `/personas/${a.contactId}`,
      });
    }

    for (const f of frentes.data?.frentes ?? []) {
      if (!f.nombre.toLowerCase().includes(texto)) continue;
      salida.push({
        id: f.id,
        tipo: 'frente',
        titulo: f.nombre,
        detalle: `${f.chats ?? 0} chats`,
        ir: `/chats?frente=${f.id}`,
      });
    }

    for (const m of mensajes.data?.mensajes ?? []) {
      salida.push({
        id: m.mensajeId,
        tipo: 'mensaje',
        // El fragmento viene con « » alrededor de lo encontrado: es
        // `ts_headline` de Postgres, que sabe donde estaba la palabra aunque
        // el dueno haya escrito el singular y el mensaje tenga el plural.
        titulo: m.fragmento.replace(/<[^>]+>/g, ''),
        detalle: `${m.conQuien} · ${m.mio ? 'lo dijiste tu' : 'te lo dijeron'}`,
        ir: `/chats/${m.conversationId}?mensaje=${m.mensajeId}`,
      });
    }

    return salida.slice(0, 24);
  }, [q, asuntos.data, frentes.data, mensajes.data]);

  if (!abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        aria-label="Buscar"
        className="mac-capsula px-3.5 text-sm text-slate-500 dark:text-slate-400"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Buscar</span>
        <kbd className="hidden rounded-[4px] bg-[var(--mac-btn)] px-1 font-sans text-[10px] sm:inline">
          ⌘K
        </kbd>
      </button>
    );
  }

  const elegir = (r: Resultado) => {
    navegar(r.ir);
    setAbierto(false);
  };

  return (
    <>
      <div
        className="mac-velo fixed inset-0 z-50"
        onClick={() => setAbierto(false)}
        aria-hidden
      />
      <div className="mac-hoja fixed inset-x-4 top-[10vh] z-50 mx-auto max-w-lg overflow-hidden rounded-xl">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 dark:border-slate-800">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            ref={campo}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, resultados.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === 'Enter' && resultados[cursor]) elegir(resultados[cursor]!);
            }}
            placeholder="Persona, tema, monto, o algo que te dijeron…"
            className="min-h-[52px] flex-1 bg-transparent text-sm outline-none"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {asuntos.isPending ? (
            <div className="p-6">
              <Spinner />
            </div>
          ) : q.trim().length < 2 ? (
            <p className="p-6 text-center text-xs text-slate-400">
              Escribe al menos dos letras. Busca personas, asuntos, montos y frentes.
            </p>
          ) : resultados.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-400">Nada con "{q}".</p>
          ) : (
            resultados.map((r, i) => (
              <button
                key={`${r.tipo}-${r.id}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => elegir(r)}
                className={cn(
                  'flex min-h-[52px] w-full items-center gap-3 px-3 text-left',
                  i === cursor && 'bg-slate-100 dark:bg-slate-800',
                )}
              >
                <span className="w-16 shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                  {r.tipo}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-slate-900 dark:text-slate-100">
                    {r.titulo}
                  </span>
                  <span className="block truncate text-xs text-slate-500">{r.detalle}</span>
                </span>
                {i === cursor && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
              </button>
            ))
          )}
        </div>

        <p className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-400 dark:border-slate-800">
          Busca personas, asuntos, montos, frentes y el texto de tus mensajes.
        </p>
      </div>
    </>
  );
}
